import React, { CSSProperties, useLayoutEffect, useState } from 'react';
import { DropdownOption } from './types';
import { MenuItem } from './MenuItem';
import _groupBy from 'lodash/groupBy';
import { GroupItem } from './GroupItem';
import { useMergedRefs } from '../hooks';
import { scrollParentToChild } from './scrollParentToChild';
import { ComponentWithCssSelector, withCssSelector } from '../shared/withCssSelector';
import { PropsWithClassName } from '../tabselector/PropsWithClassName';
import { HoverIndicator, MenuContent, MenuOverflow, MenuPanel, MenuRoot } from './Menu.styled';
import { DropdownSize, DropdownSizes, menuPadding } from './DropdownSize';

const noGroup = '__ungrouped__';

type MenuProps<T extends React.Key> = {
	value?: T | undefined;
	popperStyles: CSSProperties;
	popperAttributes: { [key: string]: string } | undefined;
	options?: DropdownOption<T>[];
	/** Rows rendered as given, instead of `options` — for a menu of actions. */
	items?: React.ReactNode;
	noOptionsMessage?: string;
	onSelect?: (id: T) => void;
	highlightedOriginalIndex?: number;
	size?: DropdownSize;
	/** Plays the collapse instead of the expand. See MenuRoot. */
	closing?: boolean;
	/** Rendered on the menu surface above the scrollport, so it never scrolls. */
	header?: React.ReactNode;
	/** Floor for the menu box — a narrow trigger need not mean a narrow menu. */
	minWidth?: number;
	/** Same plane as `header`, below the scrollport. */
	footer?: React.ReactNode;
	/** `menu` for a menu of actions; row clicks inside one are not taken as clicks on what is behind it. */
	role?: React.AriaRole;
};

function MenuComponent<T extends React.Key>({
	value,
	popperStyles,
	popperAttributes,
	onSelect,
	options = [],
	items,
	noOptionsMessage = 'No options',
	className,
	highlightedOriginalIndex,
	size,
	closing,
	header,
	footer,
	minWidth,
	role,
} : PropsWithClassName<MenuProps<T>>, menuRef: React.ForwardedRef<HTMLDivElement>) {

	const innerRef = React.useRef<HTMLDivElement>(null);
	// The box that actually scrolls — the panel around it does not.
	const overflowRef = React.useRef<HTMLDivElement>(null);

	// Where the sliding hover rectangle currently is. null parks it hidden.
	const [indicator, setIndicator] = useState<{ top: number, height: number } | null>(null);
	const moveIndicatorTo = (row: HTMLElement | null) =>
		setIndicator(row ? { top: row.offsetTop, height: row.offsetHeight } : null);

	type OptionWithIndex = DropdownOption<T> & { originalIndex?: number };

	// Use originalIndex if provided (for duplicate value handling), otherwise use array index
	const optionsWithIndex: OptionWithIndex[] = options.map((option, idx) => {
		return 'originalIndex' in option && typeof (option as OptionWithIndex).originalIndex === 'number'
			? (option as OptionWithIndex)
			: { ...option, originalIndex: idx };
	});
	const optionsByGroup = _groupBy(optionsWithIndex, option => option.group || noGroup);
	
	const renderOption = (option: OptionWithIndex) => <MenuItem
		key={`${option.value.toString()}-${option.originalIndex ?? 'unknown'}`}
		value={option.value.toString()}
		icon={option.icon}
		leading={option.leading}
		label={option.label}
		onSelect={option.onSelect || (() => onSelect?.(option.value))}
		selected={option.value === value}
		disabled={option.disabled}
		message={option.message}
		trailing={option.trailing}
		originalIndex={option.originalIndex}
		size={size}
		highlighted={highlightedOriginalIndex !== undefined && option.originalIndex === highlightedOriginalIndex}
	/>;

	const unGroupedOptions = optionsByGroup[noGroup] || [];

	useLayoutEffect(() => {
		if (value && innerRef.current && overflowRef.current) {
			const element = innerRef.current.querySelector(`[data-value="${value}"]`) as HTMLElement;
			if (element) {
				scrollParentToChild(overflowRef.current, element);
			}
		}
	}, [options, value]);

	useLayoutEffect(() => {
		if (highlightedOriginalIndex !== undefined && innerRef.current && overflowRef.current) {
			const element = 
			innerRef.current.querySelector(`[data-original-index="${highlightedOriginalIndex}"]`) as HTMLElement;
			if (element) {
				scrollParentToChild(overflowRef.current, element);
				// Keyboard moves the same rectangle the pointer does.
				moveIndicatorTo(element);
			}
		}
	}, [highlightedOriginalIndex]);

	// The height the rows ask for, handed to the scrollport so filtering the
	// list animates the menu between sizes. Measured after every render: the
	// rows only change when the menu re-renders around them.
	const contentRef = React.useRef<HTMLDivElement>(null);
	const [contentHeight, setContentHeight] = useState<number>();

	useLayoutEffect(() => {
		if (contentRef.current) {
			// Capped here as well as in CSS: a list taller than the scrollport
			// would otherwise spend its transition crossing heights that are
			// clipped to the same one, and read as a pause before the change.
			const height = contentRef.current.offsetHeight + menuPadding * 2;
			// A menu of actions (`items`) sizes to its rows — DropdownToggle lifts the CSS cap
			// too, and capping here clipped its last rows.
			setContentHeight(items ? height : Math.min(height, DropdownSizes[size ?? 'compact'].menuMaxHeight));
		}
	}, [items, options, size]);

	const menuRefMerged = useMergedRefs<HTMLElement>(menuRef, innerRef);

	return (
		<MenuRoot
			ref={menuRefMerged}
			style={popperStyles}
			$closing={closing}
			$minWidth={minWidth}
			role={role}
			{...popperAttributes}>
			<MenuPanel>
				{header}
				<MenuOverflow
					ref={overflowRef}
					className={className}
					$size={size}
					style={{ height: contentHeight }}
					onMouseOver={e => moveIndicatorTo(
						(e.target as HTMLElement).closest<HTMLElement>('[data-value]:not([data-disabled])')
					)}
					onMouseLeave={() => moveIndicatorTo(null)}>
					<HoverIndicator
						aria-hidden
						$visible={!!indicator}
						style={{
							transform: `translateY(${indicator?.top ?? 0}px)`,
							height: indicator?.height ?? 0,
						}} />
					<MenuContent ref={contentRef}>
						{items ?? (optionsWithIndex.length === 0
							? <MenuItem label={noOptionsMessage} disabled value={''} size={size} /> :
							<>
								{unGroupedOptions.map(renderOption)}
								{Object.entries(optionsByGroup).filter(([group]) => group !== noGroup)
									.map(([group, groupOptions]) => (
										<React.Fragment key={group}>
											<GroupItem label={group} />
											{groupOptions.map(renderOption)}
										</React.Fragment>
									))}
							</>
						)}
					</MenuContent>
				</MenuOverflow>
				{footer}
			</MenuPanel>
		</MenuRoot>
	);
}

// eslint-disable-next-line max-len
export const Menu = withCssSelector(React.forwardRef(MenuComponent), MenuOverflow.toString()) as unknown as (<T extends React.Key>(
	props: PropsWithClassName<MenuProps<T>> & { ref?: React.ForwardedRef<HTMLUListElement> }
  ) => ReturnType<typeof MenuComponent>) & ComponentWithCssSelector;
